import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageBreadcrumb from "@/components/base/PageBreadcrumb";
import EmptyState from "@/components/base/EmptyState";
import DetailActions from "@/components/feature/DetailActions";
import { videos } from "@/mocks/videos";
import usePageMeta from "@/hooks/usePageMeta";
import { formatDate, formatDuration, formatViews, getRelatedVideos } from "@/utils/content";

export default function VideoDetailPage() {
  const { id } = useParams();
  const video = videos.find((item) => item.id === id);
  const [notice, setNotice] = useState(false);

  usePageMeta(
    video ? `${video.title} - 挠脚心网站` : "视频不存在 - 挠脚心网站",
    video
      ? `${video.description.slice(0, 72)}`
      : "该视频不存在或已被删除，返回挠脚心网站首页继续浏览其他内容。",
    video ? `${video.tags.join(",")},挠痒视频,挠脚心网站` : "挠痒视频,挠脚心网站"
  );

  if (!video) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto flex w-full max-w-[1240px] flex-1 flex-col justify-center px-4 py-16 md:px-6">
          <EmptyState
            icon="ri-file-warning-line"
            title="该视频不存在或已被删除"
            description="可能是链接里的编号有误，也可能这条内容已经下线了。回到首页看看其他内容吧。"
            actionLabel="返回首页"
            actionTo="/"
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedVideos(video, videos);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1240px] px-4 pb-10 pt-6 md:px-6 md:pt-8">
          <PageBreadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "挠痒视频", to: "/videos" },
              { label: video.title },
            ]}
          />

          <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <article className="lg:col-span-8">
              <div className="relative overflow-hidden rounded-lg bg-foreground-950">
                <div className="relative aspect-video w-full">
                  <img
                    src={video.cover}
                    alt={`${video.title}的视频封面预览`}
                    title={`${video.title} - 挠痒视频`}
                    className="h-full w-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
                  <button
                    type="button"
                    onClick={() => setNotice(true)}
                    aria-label="播放预览提示"
                    className="absolute inset-0 flex cursor-pointer items-center justify-center"
                  >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-foreground-950 transition-transform duration-300 hover:scale-105">
                      <i className="ri-play-fill text-2xl"></i>
                    </span>
                  </button>
                  <span className="absolute bottom-4 left-4 flex items-center gap-2 rounded-md bg-foreground-950/70 px-2.5 py-1.5 text-xs text-background-50">
                    <i className="ri-time-line"></i>
                    {formatDuration(video.duration)}
                  </span>
                </div>
              </div>

              {notice && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-foreground-800">
                  <i className="ri-information-line mt-0.5 text-base text-primary-700"></i>
                  <p className="flex-1 leading-relaxed">
                    这是演示站点，视频仅提供封面预览，暂不支持真实播放。你可以继续阅读下方介绍或浏览相关推荐。
                  </p>
                  <button
                    type="button"
                    onClick={() => setNotice(false)}
                    aria-label="关闭提示"
                    className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-800"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              )}

              <div className="mt-6">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-800">
                    {video.category}
                  </span>
                  <Link
                    to="/videos"
                    className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-foreground-900"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                    返回{video.category}
                  </Link>
                </div>
                <h1 className="mt-3 font-heading text-2xl leading-snug text-foreground-950 md:text-3xl">
                  {video.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500 md:text-sm">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-100 text-[11px] text-secondary-800">
                      {video.author.slice(0, 1)}
                    </span>
                    {video.author}
                  </span>
                  <span className="whitespace-nowrap">上传于 {formatDate(video.date)}</span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i>
                    {formatViews(video.views)} 次浏览
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-fire-line"></i>
                    热度 {video.heat}
                  </span>
                </div>

                <div className="mt-5">
                  <DetailActions favoriteKey={`video:${video.id}`} title={video.title} />
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                  <h2 className="font-heading text-lg text-foreground-950">视频介绍</h2>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                    {video.description}
                  </p>
                  <div className="mt-5 border-t border-background-100 pt-4">
                    <p className="mb-2 text-xs text-foreground-500">相关标签</p>
                    <div className="flex flex-wrap gap-2">
                      {video.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-start gap-2 rounded-md bg-background-100 px-4 py-3 text-xs leading-relaxed text-foreground-600">
                  <i className="ri-shield-check-line mt-0.5 text-base text-secondary-600"></i>
                  提示：本站所有视频均为虚构演示内容，不含真实播放资源；任何互动体验都应以双方自愿、随时可停止为前提。
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-heading text-base text-foreground-950">
                    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className="ri-magic-line text-sm"></i>
                    </span>
                    相关推荐
                  </h2>
                  <span className="text-[11px] text-foreground-400">同标签优先</span>
                </div>
                <div className="flex flex-col gap-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/videos/detail/${item.id}`}
                      className="group flex gap-3 rounded-md p-1 transition-colors hover:bg-background-100"
                    >
                      <div className="relative h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title}的封面缩略图`}
                          title={`${item.title} - 挠痒视频`}
                          className="h-full w-full object-cover object-top"
                        />
                        <span className="absolute bottom-1 right-1 rounded bg-foreground-950/70 px-1 py-0.5 text-[10px] text-background-50">
                          {formatDuration(item.duration)}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-2-custom text-sm leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </p>
                        <p className="mt-1.5 text-[11px] text-foreground-500">
                          {item.category} · {formatDate(item.date)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/videos"
                  className="mt-4 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-xs text-foreground-700 transition-colors hover:bg-background-100"
                >
                  浏览更多{video.category}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-secondary-50 p-5">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                  <i className="ri-heart-3-line text-base text-accent-600"></i>
                  喜欢这个主题？
                </p>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  社区里有不少同好正在分享经验，也有适合新手的入门建议。
                </p>
                <Link
                  to="/community"
                  className="mt-3 flex w-fit items-center gap-1 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-secondary-600"
                >
                  进入社区
                  <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}